import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { flagEmoji } from "@/components/IpGeoChip";
import {
  GlobeIcon,
  ClockIcon,
  ShieldCheckIcon,
  CrownSimpleIcon,
  ProhibitIcon,
  WarningIcon,
  CopyIcon,
  CalendarIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Full-detail audit panel for a single user.
 * Surfaces every admin-only field the API returns:
 *   - Account & profile
 *   - Signup / verification / last-seen IPs with full GeoIP (country, city,
 *     region, ISP, ASN, private-flag) — three side-by-side cards
 *   - Risk score + every triggered signal with severity dot
 *   - Subscription + Founder state
 *   - Ban metadata
 *
 * Designed to be opened from the Users tab via a "Details" button on any row.
 */
export default function UserDetailsDialog({ user, onClose }) {
  if (!user) return null;

  const copy = (txt) => {
    if (!txt) return;
    navigator.clipboard.writeText(String(txt));
    toast.success(`Copied ${txt}`);
  };

  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="admin-user-details-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <div className="h-12 w-12 bg-zinc-100 rounded-xl grid place-items-center font-bold text-lg">
              {user.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-lg font-bold leading-tight">
                {user.name}
                {user.username && (
                  <span className="text-zinc-400 font-normal ml-2">
                    @{user.username}
                  </span>
                )}
              </div>
              <div className="text-xs font-mono text-zinc-500 truncate">
                {user.email}
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {user.role === "admin" && (
                <span className="pill bg-red-600 text-white text-[10px]">
                  ADMIN
                </span>
              )}
              {user.is_founder && (
                <span className="pill bg-amber-500 text-white text-[10px] inline-flex items-center gap-1">
                  <CrownSimpleIcon size={10} weight="fill" />
                  FOUNDER
                </span>
              )}
              {user.banned_at && (
                <span className="pill bg-red-700 text-white text-[10px] inline-flex items-center gap-1">
                  <ProhibitIcon size={10} weight="fill" />
                  BANNED
                </span>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* ── Risk panel ─────────────────────────────── */}
          {user.risk && user.risk.score > 0 ? (
            <Section
              icon={
                <WarningIcon
                  size={14}
                  weight="fill"
                  className={
                    user.risk.level === "high"
                      ? "text-red-600"
                      : "text-amber-600"
                  }
                />
              }
              title={`Risk · ${user.risk.score} (${user.risk.level})`}
              tone={user.risk.level === "high" ? "danger" : "warning"}
              testid="admin-user-details-risk"
            >
              {user.risk.signals && user.risk.signals.length > 0 ? (
                <ul className="space-y-1.5">
                  {user.risk.signals.map((s, i) => (
                    <li
                      key={`${s.type}-${i}`}
                      className="text-[12px] flex items-start gap-2"
                    >
                      <span
                        className={`mt-1 h-1.5 w-1.5 rounded-full shrink-0 ${
                          s.severity === "high"
                            ? "bg-red-500"
                            : s.severity === "medium"
                            ? "bg-amber-500"
                            : "bg-zinc-400"
                        }`}
                      />
                      <span className="text-zinc-700">{s.message}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[12px] text-zinc-500">No detail signals.</p>
              )}
            </Section>
          ) : (
            <Section
              icon={
                <ShieldCheckIcon
                  size={14}
                  weight="fill"
                  className="text-emerald-600"
                />
              }
              title="Risk · clean"
              tone="success"
              testid="admin-user-details-risk-clean"
            >
              <p className="text-[12px] text-zinc-600">
                No risk signals triggered for this account.
              </p>
            </Section>
          )}

          {/* ── IPs / GeoIP ───────────────────────────── */}
          <Section
            icon={<GlobeIcon size={14} weight="fill" className="text-[#1D78AB]" />}
            title="IP audit · GeoIP"
            testid="admin-user-details-ips"
          >
            {!user.signup_ip && !user.verification_ip && !user.last_seen_ip ? (
              <p className="text-[12px] text-zinc-500">
                No IPs recorded for this account.{" "}
                <span className="text-zinc-400">
                  (Likely seeded directly to the database — only real signups
                  through the registration endpoint capture IPs.)
                </span>
              </p>
            ) : (
              <div className="grid sm:grid-cols-3 gap-3">
                <IpCard
                  label="Signup"
                  ip={user.signup_ip}
                  geo={user.signup_ip_geo}
                  copy={copy}
                />
                <IpCard
                  label="ID verification"
                  ip={user.verification_ip}
                  geo={user.verification_ip_geo}
                  copy={copy}
                />
                <IpCard
                  label="Last seen"
                  ip={user.last_seen_ip}
                  geo={user.last_seen_ip_geo}
                  copy={copy}
                />
              </div>
            )}
            {user.signup_user_agent && (
              <div className="mt-3 pt-3 border-t border-zinc-100">
                <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-1">
                  Browser at signup
                </div>
                <div className="text-[11px] font-mono text-zinc-600 break-all">
                  {user.signup_user_agent}
                </div>
              </div>
            )}
          </Section>

          {/* ── Account ────────────────────────────────── */}
          <Section
            icon={<CalendarIcon size={14} weight="fill" className="text-zinc-600" />}
            title="Account"
            testid="admin-user-details-account"
          >
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-[12px]">
              <Field label="User ID" value={user.user_id} mono onCopy={() => copy(user.user_id)} />
              <Field label="Account type" value={user.account_type?.replace("_", " ")} />
              <Field label="Segment" value={user.segment} />
              <Field
                label="Verification"
                value={user.verification_status}
                tone={
                  user.verification_status === "approved"
                    ? "success"
                    : user.verification_status === "rejected"
                    ? "danger"
                    : user.verification_status === "pending"
                    ? "warning"
                    : "neutral"
                }
              />
              <Field label="Created" value={fmtDate(user.created_at)} />
              <Field label="Last seen" value={fmtDate(user.last_seen_at)} />
              {user.role === "admin" && (
                <Field label="Role" value="admin" tone="danger" />
              )}
            </div>
          </Section>

          {/* ── Subscription / Founder ─────────────────── */}
          {(user.is_founder || user.plus_status || user.plus_until) && (
            <Section
              icon={<CrownSimpleIcon size={14} weight="fill" className="text-amber-500" />}
              title="Subscription · Founder"
              testid="admin-user-details-subscription"
            >
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-[12px]">
                {user.is_founder && (
                  <Field
                    label="Founder since"
                    value={fmtDate(user.founder_since) || "yes"}
                    tone="success"
                  />
                )}
                {user.plus_status && (
                  <Field label="Plus status" value={user.plus_status} />
                )}
                {user.plus_plan && (
                  <Field label="Plus plan" value={user.plus_plan} />
                )}
                {user.plus_until && (
                  <Field label="Plus until" value={fmtDate(user.plus_until)} />
                )}
                {Number.isFinite(user.credit_balance_cents) && (
                  <Field
                    label="Credit balance"
                    value={`$${(user.credit_balance_cents / 100).toFixed(2)}`}
                  />
                )}
              </div>
            </Section>
          )}

          {/* ── Ban audit ──────────────────────────────── */}
          {user.banned_at && (
            <Section
              icon={<ProhibitIcon size={14} weight="fill" className="text-red-600" />}
              title="Ban audit"
              tone="danger"
              testid="admin-user-details-ban"
            >
              <div className="space-y-1.5 text-[12px]">
                <Field label="Banned at" value={fmtDate(user.banned_at)} />
                {user.banned_by && <Field label="Banned by" value={user.banned_by} mono />}
                {user.banned_reason && (
                  <div className="mt-1">
                    <div className="text-[10px] uppercase tracking-wider text-red-700 font-bold">
                      Reason
                    </div>
                    <div className="italic text-red-700">{user.banned_reason}</div>
                  </div>
                )}
              </div>
            </Section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────── helpers ─────────────────────────── */

function Section({ icon, title, tone, children, testid }) {
  const ring =
    tone === "danger"
      ? "border-red-200 bg-red-50/50"
      : tone === "warning"
      ? "border-amber-200 bg-amber-50/50"
      : tone === "success"
      ? "border-emerald-200 bg-emerald-50/50"
      : "border-zinc-200 bg-white";
  return (
    <div
      className={`rounded-xl border ${ring} p-3`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-zinc-700 mb-2">
        {icon}
        <span>{title}</span>
      </div>
      {children}
    </div>
  );
}

function IpCard({ label, ip, geo, copy }) {
  if (!ip) {
    return (
      <div className="rounded-lg border border-dashed border-zinc-200 p-2.5 text-center">
        <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">
          {label}
        </div>
        <div className="text-[11px] text-zinc-300 italic mt-1">none recorded</div>
      </div>
    );
  }
  const flag = flagEmoji(geo?.country_code);
  const place = [geo?.city, geo?.country].filter(Boolean).join(", ");
  return (
    <div
      className="rounded-lg border border-zinc-200 bg-white p-2.5"
      data-testid={`admin-user-details-ip-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">
        {label}
      </div>
      <div className="mt-1 flex items-center gap-1.5">
        {flag && <span className="text-base leading-none">{flag}</span>}
        <span className="text-[12px] font-medium text-zinc-700">
          {place || (geo?.private ? "Private network" : "Unknown")}
        </span>
      </div>
      <button
        type="button"
        onClick={() => copy(ip)}
        className="mt-1 inline-flex items-center gap-1 text-[11px] font-mono text-zinc-500 hover:text-[#1D78AB]"
        title="Copy IP"
      >
        {ip}
        <CopyIcon size={10} />
      </button>
      {(geo?.region || geo?.isp || geo?.asn) && (
        <div className="mt-1.5 pt-1.5 border-t border-zinc-100 space-y-0.5 text-[10px] text-zinc-500 leading-snug">
          {geo.region && <div>Region: {geo.region}</div>}
          {geo.isp && <div>ISP: {geo.isp}</div>}
          {geo.asn && <div>ASN: {geo.asn}</div>}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono, tone, onCopy }) {
  if (value === null || value === undefined || value === "") return null;
  const toneCls =
    tone === "success"
      ? "text-emerald-700"
      : tone === "danger"
      ? "text-red-700"
      : tone === "warning"
      ? "text-amber-700"
      : "text-zinc-800";
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">
        {label}
      </span>
      <button
        type="button"
        disabled={!onCopy}
        onClick={onCopy}
        className={`${toneCls} ${mono ? "font-mono text-[11px]" : "text-[12px]"} text-right truncate ${
          onCopy ? "hover:text-[#1D78AB] cursor-pointer" : "cursor-default"
        }`}
        title={onCopy ? "Click to copy" : undefined}
      >
        {value}
      </button>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
